import { apiFetcher } from "@/services/API.service";
import {
  FullTimer,
  FullTimerSchema,
  OrganizedTimersEvent,
  UserTimer,
  UserTimerSchema,
} from "@/types";
import {
  validateArrayWithSchema,
  validateWithSchema,
} from "@/utils/dataValidators.zod";

// #region fetchUserTimers
export async function fetchUserTimers({
  signal,
  userID,
  retry = false,
}: {
  userID: number;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      timers: UserTimer[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchUserTimers");
    }

    const response = await apiFetcher({
      route: "/timers/get/userTask",
      method: "POST",
      options: {
        body: {},
      },
      signal: signal,
    });

    console.log("response", response);

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<UserTimer>(
        response.data,
        UserTimerSchema,
        "UserTimer"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not an array of valid timers",
        };
      }

      return {
        rStatus: "success",
        timers: validatedData,
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        timers: [],
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return fetchUserTimers({ signal, userID, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion fetchUserTimers

// #region sendNewUserTimer
export async function sendNewUserTimer({
  taskID,
  name,
  startTime,
  endTime,
}: {
  taskID: number | null;
  name: string;
  startTime: string;
  endTime: string | null;
}): Promise<
  | {
      rStatus: "success";
      rData: UserTimer;
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/timer/create",
      method: "POST",
      options: {
        body: {
          task_id: taskID,
          name: name,
          events: [
            {
              start: startTime,
              end: endTime ? endTime : undefined,
            },
          ],
        },
      },
    });

    if (response.status === 200) {
      const validatedData = validateWithSchema<UserTimer>(
        response.data,
        UserTimerSchema,
        "UserTimer"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not a valid timer",
        };
      }

      return {
        rStatus: "success",
        rData: validatedData,
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendNewUserTimer

// #region sendDeleteUserTimer
// export async function sendDeleteUserTimer({
//   timerID,
// }: {
//   timerID: number;
// }): Promise<
//   | {
//       rStatus: "success";
//     }
//   | {
//       rStatus: "error";
//       message: string;
//     }
// > {
//   try {
//     const response = await apiFetcher({
//       route: "/timer/delete",
//       method: "POST",
//       options: {
//         body: {
//           id: timerID,
//         },
//       },
//     });

//     if (
//       response.status === 200 ||
//       response.status === 202 ||
//       response.status === 204
//     ) {
//       return { rStatus: "success" };
//     }

//     if (response.status === 401 || response.status === 403) {
//       return { rStatus: "error", message: "unauthorized" };
//     }

//     if (response.status === 404) {
//       return { rStatus: "error", message: "route not found" };
//     }

//     if (response.status === 0) {
//       if (response.error === "request_aborted") {
//         return { rStatus: "error", message: "request_aborted" };
//       }

//       return {
//         rStatus: "error",
//         message: "network_error",
//       };
//     }

//     return { rStatus: "error", message: "failed to fetch" };
//   } catch (error) {
//     console.error(error);
//     return { rStatus: "error", message: "failed to fetch" };
//   }
// }

// #endregion sendDeleteUserTimer

// #region FetchAllTimers
export async function fetchAllTimers({
  signal,
  retry = false,
}: {
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      timers: FullTimer[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchAllTimers");
    }

    const response = await apiFetcher({
      route: "/timers/get/all",
      method: "POST",
      signal: signal,
    });

    console.log(response);

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<FullTimer>(
        response.data,
        FullTimerSchema,
        "Full Timer"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not an array of valid timers",
        };
      }

      return {
        rStatus: "success",
        timers: validatedData,
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        timers: [],
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return fetchAllTimers({ signal, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

// #region fetchMyTimers
export async function fetchMyTimers({
  signal,
  retry = false,
}: {
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      timers: FullTimer[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchMyTimers");
    }

    const response = await apiFetcher({
      route: "/timers/get",
      method: "POST",
      signal: signal,
    });

    console.log(response);

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<FullTimer>(
        response.data,
        FullTimerSchema,
        "Full Timer"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not an array of valid timers",
        };
      }

      return {
        rStatus: "success",
        timers: validatedData,
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        timers: [],
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return fetchMyTimers({ signal, retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion fetchMyTimers

// #endregion FetchAllTimers

export async function updateTimer({
  timer,
}: {
  timer: OrganizedTimersEvent;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    console.log("timer", timer);

    const response = await apiFetcher({
      route: "/timer/update",
      method: "POST",
      options: {
        body: {
          ...timer,
          timer: {
            ...timer.timer,
            tags: undefined,
          },
        },
      },
    });

    console.log(response);

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    return { rStatus: "error", message: "failed to update" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to update" };
  }
}

export async function sendStopTimer(
  timerID: number,
  endTimer: string
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/timer/stop",
      method: "POST",
      options: {
        body: {
          id: timerID,
          end_time: endTimer,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    return { rStatus: "error", message: "failed to stop" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to stop" };
  }
}

export async function sendStartTimer(
  timerID: number,
  startTimer: string
): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/timer/start",
      method: "POST",
      options: {
        body: {
          id: timerID,
          start_time: startTimer,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    return { rStatus: "error", message: "failed to start" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to start" };
  }
}

// #region sendDeleteUserTimerEvent
export async function sendDeleteUserTimerEvent({
  timerID,
  eventPosition,
  eventStartTime,
}: {
  timerID: number;
  eventPosition: number;
  eventStartTime: string;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/timer/delete",
      method: "POST",
      options: {
        body: {
          id: timerID,
          event_position: eventPosition,
          event_start_time: eventStartTime,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return {
        rStatus: "success",
      };
    }

    if (response.status === 204) {
      return { rStatus: "error", message: "timer event not found" };
    }

    if (response.status === 400) {
      return {
        rStatus: "error",
        message: "bad request. Please contact support",
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 409) {
      return {
        rStatus: "error",
        message: "timer start does not match position. Please refresh timers",
      };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 500) {
      return { rStatus: "error", message: "internal server error" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendDeleteUserTimerEvent
